import React, { FC, ReactNode, useMemo } from 'react'
import { Flex, Heading, Select, SelectProps } from '../index'
import { AnswerId, QuestionType, ScenarioStep } from '../../state/interfaces'
import { DefaultCheckboxes, SmallPillCheckboxes, CheckboxesConfirm } from '../checkboxes'
import { ImageRadioButton, RadioButtonGroup, RadioButtonGroupProps } from '../radio-buttons'
import { Text, VStack } from '@chakra-ui/react'
import { SmallPillRadioButton } from '../radio-buttons/small-pill-radio-button'
import { SmallPillVerticalRadioButton } from '../radio-buttons/small-pill-vertical-radio-button'
import { LargePillRadioButton } from '../radio-buttons/large-pill-radio-button'
import { LargePillRadioButtonWithOptions } from '../radio-buttons/large-pill-radio-button-with-options'
import { MixedLargePillRadioButton } from '../radio-buttons/mixed-large-pill-radio-button'
import { InternetUsage, InternetUsageInfoOnly } from '../internet-usage'
import {
  InputDefault,
  InputAutoComplete,
  GoogleAutocomplete,
  InputEmail,
  InputPhoneNumber,
  InputCurrency,
  InputPostalCode,
} from '../input'
import { StarsReview } from '../stars/stars-review'
import { RadioConfirm } from '../radio-buttons/radio-confirm'
import { MainPillRadioButton } from '../radio-buttons/main-pill-radio-button'
import { leadCheckavailability } from '../leadCheckAvailability'
import { alertCriteria } from '../alert-criterea'
import { TextareaDefault, TextareaReferralCode } from '../textarea'
import { SliderSavings } from '../slider/slider'
import { SliderData } from '../slider/sliderData'
import { InputSavings } from '../input/input-savings'
import { providerAvailability } from '../leadCheckAvailability/providerAvailability'
import { SmallPillRadioButtonImage } from '../radio-buttons/small-radio-button-image'
import { Trans, useTranslation } from 'react-i18next'
import { RadioButtonDefault } from '../radio-buttons/radio-button-default'
import { MixedLargePillRadioButtonProvince } from '../radio-buttons/mixed-large-pill-radio-button-province'
import { MixedLargePillRadioButtonDecision } from '../radio-buttons/mixed-large-pill-radio-button-decision'
import { InputDatePicker } from '../input/input-date-picker'
import { CheckboxesConfirmOnlyNewsLetter } from '../checkboxes/checkboxes-confirm-only-newsletter'
import { PostalCodeStandalone } from '../input/postal-code-standalone'
import { ExtraLargePillRadioButton } from '../radio-buttons/extra-large-pill-radio-button'
import { newProviderVerification } from '../leadCheckAvailability/newProviderVerification'
import { MainPillWithAdditionalButtonRadioButton } from '../radio-buttons/main-pill-with-additional-button-radio-button'

export interface SearchStepProps {
  question: ScenarioStep['question']
  questionType?: ScenarioStep['type']
  answerPlaceholder?: ScenarioStep['answerPlaceholder']
  answers: ScenarioStep['answers']
  currentAnswers?: Array<AnswerId>
  onChange: (answerIds: Array<AnswerId>) => void
  clearNext: (answerIds: Array<AnswerId>) => void
  subtext?: ReactNode
  stepId: string
  header?: string
  metadata?: ScenarioStep['metadata']
}

/**
 * This component serves as generic "step" to display questions that don't require anything fancy to be displayed.
 */
export const SearchStep: FC<SearchStepProps> = ({
  question,
  onChange,
  clearNext,
  answers,
  answerPlaceholder,
  currentAnswers,
  questionType,
  subtext,
  stepId,
  metadata,
}) => {
  const { t } = useTranslation()
  const renderedFormElement = useMemo(() => {
    let Component: FC<any> = () => <></>
    let props: SelectProps | RadioButtonGroupProps | Record<string, unknown> = {
      onChange,
      value: currentAnswers ? currentAnswers[0] : undefined,
    }

    /**
     * Should we build this as a chain of responsibility? I feel like we're stretching the limits of reasonable use of a
     * switch/case.
     */
    switch (questionType) {
      case QuestionType.CHOICE:
        Component = Select
        props = {
          ...props,
          placeholder: answerPlaceholder,
          options: answers.map(({ id, text }) => ({ label: text, value: id })),
        }
        break
      case QuestionType.CHOICE_AS_MULTISELECT_PILLS:
        Component = SmallPillCheckboxes
        props = {
          ...props,
          choices: answers.map(({ id, text }) => ({ label: text, value: id })),
          onChange: clearNext,
          onConfirm: onChange,
        }
        break
      case QuestionType.CHOICE_AS_MULTISELECT_DEFAULT:
        Component = DefaultCheckboxes
        props = {
          ...props,
          choices: answers.map(({ id, text, metadata }) => ({
            label: text,
            value: id,
            isChecked: metadata ? (metadata['isChecked'] ? metadata['isChecked'] : null) : null,
            group: metadata ? (metadata['group'] ? metadata['group'] : null) : null,
          })),
          onChange: clearNext,
          onConfirm: onChange,
          selectedChoices: answers.map(({ id, text }) => ({ label: text, value: id })),
        }
        break

      case QuestionType.CHOICE_BOXES:
      case QuestionType.CHOICE_AS_IMAGES:
        Component = RadioButtonGroup
        props = {
          ...props,
          // component: questionType === QuestionType.CHOICE_BOXES ? TextBoxRadioButton : ImageRadioButton,
          component: ImageRadioButton,
          name: stepId,
          justify: questionType === QuestionType.CHOICE_BOXES ? 'center' : 'space-around',
          choices: answers.map(({ id, text, metadata }) => ({
            label: text,
            value: id,
            image: metadata?.image,
            imageSelected: metadata?.imageSelected,
            isChecked: false,
          })),
        }
        break
      case QuestionType.CHOICE_AS_PILLS:
        Component = RadioButtonGroup
        props = {
          ...props,
          component: SmallPillRadioButton,
          name: stepId,
          justify: 'space-around',
          choices: answers.map(({ id, text }) => ({ label: text, value: id, isChecked: false })),
        }
        break
      case QuestionType.CHOICE_AS_PILLS_IMAGE:
        Component = RadioButtonGroup
        props = {
          ...props,
          component: SmallPillRadioButtonImage,
          name: stepId,
          justify: 'space-around',
          choices: answers.map(({ id, text, metadata }) => ({
            label: text,
            value: id,
            image: metadata?.image,
            initials: metadata?.initials,
            isChecked: false,
          })),
        }
        break
      case QuestionType.CHOICE_AS_PILLS_LARGE:
        Component = RadioButtonGroup
        props = {
          ...props,
          component: LargePillRadioButton,
          name: stepId,
          justify: 'space-around',
          direction: 'vertical',
          choices: answers.map(({ id, text, metadata }) => ({
            label: text,
            value: id,
            isChecked: false,
            metadata: metadata ? metadata : null,
          })),
          grandImage: metadata?.grandImage,
        }
        break
      case QuestionType.CHOICE_AS_PILLS_EXTRA_LARGE:
        Component = RadioButtonGroup
        props = {
          ...props,
          component: ExtraLargePillRadioButton,
          name: stepId,
          justify: 'space-around',
          direction: 'vertical',
          choices: answers.map(({ id, text, metadata }) => ({
            label: text,
            value: id,
            isChecked: false,
            metadata: metadata ? metadata : null,
          })),
          grandImage: metadata?.grandImage,
        }
        break
      case QuestionType.CHOICE_AS_PILLS_LARGE_WITH_OPTIONS:
        Component = RadioButtonGroup
        props = {
          ...props,
          component: LargePillRadioButtonWithOptions,
          name: stepId,
          justify: 'space-around',
          direction: 'vertical',
          options: [
            { id: 'canadausa', text: t('common_us-calling-data'), metadata: { isChecked: false, group: 'options' } },
            { id: 'national', text: t('common_national-calling'), metadata: { isChecked: false, group: 'options' } },
          ].map(({ id, text, metadata }) => ({
            label: text,
            value: id,
            isChecked: metadata ? (metadata['isChecked'] ? metadata['isChecked'] : null) : null,
            group: metadata ? (metadata['group'] ? metadata['group'] : null) : null,
          })),
          choices: answers.map(({ id, text }) => ({ label: text, value: id, isChecked: false })),
        }
        break
      case QuestionType.CHOICE_AS_PILLS_VERTICAL:
        Component = RadioButtonGroup
        props = {
          ...props,
          component: SmallPillVerticalRadioButton,
          name: stepId,
          justify: 'space-around',
          direction: 'vertical',
          choices: answers.map(({ id, text }) => ({ label: text, value: id, isChecked: false })),
        }
        break
      case QuestionType.MIXED_CHOICE_WITH_PILLS_LARGE:
        Component = RadioButtonGroup
        props = {
          ...props,
          component: MixedLargePillRadioButton,
          name: stepId,
          justify: 'space-around',
          choices: answers.map(({ id, text, metadata }) => ({
            label: text,
            value: id,
            image: metadata?.image,
            blueImage: metadata?.blueImage,
            isChecked: false,
          })),
        }
        break
      case QuestionType.MIXED_CHOICE_WITH_PILLS_LARGE_PROVINCE:
        Component = RadioButtonGroup
        props = {
          ...props,
          component: MixedLargePillRadioButtonProvince,
          name: stepId,
          justify: 'space-around',
          choices: answers.map(({ id, text, metadata }) => ({
            label: text,
            value: id,
            image: metadata?.image,
            initials: metadata?.initials,
            isChecked: false,
          })),
        }
        break
      case QuestionType.MIXED_CHOICE_WITH_PILLS_LARGE_DECISION:
        Component = RadioButtonGroup
        props = {
          ...props,
          component: MixedLargePillRadioButtonDecision,
          name: stepId,
          justify: 'space-around',
          choices: answers.map(({ id, text, metadata }) => ({
            label: text,
            secondaryLabel: metadata?.secondaryLabel,
            value: id,
            image: metadata?.image,
            imageSelected: metadata?.imageSelected,
            initials: metadata?.initials,
            isChecked: false,
          })),
        }
        break
      case QuestionType.POSTAL_CODE_INTERNET:
        Component = InputPostalCode
        props = {
          ...props,
          name: stepId,
          onChange: onChange,
        }
        break
      case QuestionType.POSTAL_CODE_INTERNET_USAGE:
        Component = InternetUsage
        props = {
          name: stepId,
          grandImage: metadata?.grandImage,
        }
        break
      case QuestionType.INTERNET_USAGE_INFO_ONLY:
        Component = InternetUsageInfoOnly
        break
      case QuestionType.INPUT_DEFAULT:
        Component = InputDefault
        props = {
          ...props,
          name: stepId,
          placeholder: answerPlaceholder,
          choices: answers.map(({ id }) => ({ value: id, isChecked: false })),
          grandImage: metadata?.grandImage,
          inputType: metadata?.inputType,
        }
        break
      case QuestionType.POSTAL_CODE_STANDALONE:
        Component = PostalCodeStandalone
        props = {
          ...props,
          name: stepId,
          placeholder: answerPlaceholder,
          choices: answers.map(({ id }) => ({ value: id, isChecked: false })),
          grandImage: metadata?.grandImage,
          onChange: onChange,
        }
        break
      case QuestionType.INPUT_AUTOCOMPLETE:
        Component = InputAutoComplete
        props = {
          ...props,
          name: stepId,
          placeholder: answerPlaceholder,
          choices: answers.map(({ id }) => ({ value: id, isChecked: false })),
          grandImage: metadata?.grandImage,
        }
        break
      case QuestionType.GOOGLE_AUTOCOMPLETE:
        Component = GoogleAutocomplete
        props = {
          ...props,
          placeholder: answerPlaceholder,
          choices: answers.map(({ id }) => ({ value: id, isChecked: false })),
          grandImage: metadata?.grandImage,
        }
        break
      case QuestionType.STARS:
        Component = StarsReview
        props = {
          ...props,
          choices: answers.map(({ id }) => ({ value: id, isChecked: false })),
          grandImage: metadata?.grandImage,
        }
        break
      case QuestionType.TEXTAREA:
        Component = TextareaDefault
        props = {
          ...props,
          name: stepId,
          placeholder: answerPlaceholder,
          choices: answers.map(({ id }) => ({ value: id, isChecked: false })),
          grandImage: metadata?.grandImage,
        }
        break
      case QuestionType.INPUT_DATE_PICKER:
        Component = InputDatePicker
        props = {
          ...props,
          name: stepId,
          placeholder: answerPlaceholder,
          choices: answers.map(({ id }) => ({ value: id, isChecked: false })),
          grandImage: metadata?.grandImage,
        }
        break
      case QuestionType.TEXTAREA_REFERRAL_CODE:
        Component = TextareaReferralCode
        props = {
          ...props,
          name: stepId,
          placeholder: answerPlaceholder,
          choices: answers.map(({ id }) => ({ value: id, isChecked: false })),
        }
        break
      case QuestionType.RADIO_CONFIRM:
        Component = RadioConfirm
        props = {
          ...props,
          placeholder: answerPlaceholder,
          choices: answers.map(({ id }) => ({ value: id, isChecked: false })),
        }
        break
      case QuestionType.RADIO_DEFAULT:
        Component = RadioButtonDefault
        props = {
          ...props,
          name: stepId,
          onChange: clearNext,
          onConfirm: onChange,
          choices: answers.map(({ id, text }) => ({ label: text, value: id })),
          grandImage: metadata?.grandImage,
        }
        break
      case QuestionType.CHECKBOX_CONFIRM:
        Component = CheckboxesConfirm
        props = {
          ...props,
          onConfirm: onChange,
          grandImage: metadata?.grandImage,
        }
        break
      case QuestionType.CHECKBOX_CONFIRM_VISITOR:
        Component = CheckboxesConfirmOnlyNewsLetter
        props = {
          ...props,
          onConfirm: onChange,
          grandImage: metadata?.grandImage,
        }
        break
      case QuestionType.EMAIL:
        Component = InputEmail
        props = {
          ...props,
          name: stepId,
          placeholder: answerPlaceholder,
          choices: answers.map(({ id }) => ({ value: id, isChecked: false })),
          grandImage: metadata?.grandImage,
        }
        break
      case QuestionType.CHOICE_AS_MAIN_PILL:
        Component = RadioButtonGroup
        props = {
          ...props,
          component: MainPillRadioButton,
          name: stepId,
          choices: answers.map(({ id, text }) => ({ label: text, value: id, isChecked: false })),
          grandImage: metadata?.grandImage,
        }
        break
      case QuestionType.CHOICE_AS_MAIN_PILL_ADDITIONAL_BUTTON:
        Component = RadioButtonGroup
        props = {
          ...props,
          component: MainPillWithAdditionalButtonRadioButton,
          name: stepId,
          choices: answers.map(({ id, text }) => ({ label: text, value: id, isChecked: false })),
          grandImage: metadata?.grandImage,
        }
        break
      case QuestionType.LEAD:
        Component = leadCheckavailability
        props = {
          ...props,
          component: MainPillRadioButton,
          name: stepId,
          choices: answers.map(({ id, text }) => ({ label: text, value: id, isChecked: false })),
        }
        break
      case QuestionType.INPUT_PHONE:
        Component = InputPhoneNumber
        props = {
          ...props,
          name: stepId,
          placeholder: answerPlaceholder,
          choices: answers.map(({ id }) => ({ value: id, isChecked: false })),
          grandImage: metadata?.grandImage,
        }
        break
      case QuestionType.ALERT_CRITERIA:
        Component = alertCriteria
        props = {
          ...props,
          component: MainPillRadioButton,
          name: stepId,
          choices: answers.map(({ id, text }) => ({ label: text, value: id, isChecked: false })),
          grandImage: metadata?.grandImage,
        }
        break
      case QuestionType.SLIDER:
        Component = SliderSavings
        props = {
          ...props,
          name: stepId,
          onChange: onChange,
        }
        break
      case QuestionType.SLIDER_DATA:
        Component = SliderData
        props = {
          ...props,
          name: stepId,
          onChange: onChange,
          metadata: metadata,
        }
        break
      case QuestionType.INPUT_CURRENCY:
        Component = InputCurrency
        props = {
          ...props,
          name: stepId,
          onChange: onChange,
        }
        break
      case QuestionType.INPUT_SAVINGS:
        Component = InputSavings
        props = {
          ...props,
          name: stepId,
          onChange: onChange,
        }
        break
      case QuestionType.PROVIDER_AVAILABILITY:
        Component = providerAvailability
        props = {
          ...props,
          component: MainPillRadioButton,
          name: stepId,
          choices: answers.map(({ id, text }) => ({ label: text, value: id, isChecked: false })),
        }
        break
      case QuestionType.NEW_CLIENT_VERIFICATION:
        Component = newProviderVerification
        props = {
          ...props,
          component: LargePillRadioButton,
          name: stepId,
          choices: answers.map(({ id, text }) => ({ label: text, value: id, isChecked: false })),
        }
        break
    }

    return <Component {...props} />
  }, [answers, onChange, clearNext, currentAnswers, answerPlaceholder, questionType, stepId, metadata])

  return (
    <Flex flex="1" flexDirection="column" alignItems="center" tabIndex={-1}>
      {(question || subtext) && (
        <VStack spacing={2} tabIndex={-1}>
          {question && (
            <Heading level={3}>
              <Trans>{question}</Trans>
            </Heading>
          )}
          {subtext && (
            <Text color="darkGrey" fontSize="xs" paddingX={4} paddingY={2} textAlign="center">
              {subtext}
            </Text>
          )}
        </VStack>
      )}
      {renderedFormElement}
    </Flex>
  )
}
